import {
    Component,
    lazy,
    type ErrorInfo,
    type KeyboardEvent,
    type ReactNode,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll } from "motion/react";

const LazyWorld3DCanvas = lazy(async () => {
    const module = await import("./world-3d-canvas");
    return { default: module.World3DCanvas };
});

const STOPS = ["care", "track", "quote", "read", "plan"] as const;

type ErrorBoundaryProps = { children: ReactNode; fallback: ReactNode };
type ErrorBoundaryState = { hasError: boolean };

class World3DErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.warn("[World3DSection] WebGL/texture error:", error.message, info.componentStack);
    }

    render() {
        if (this.state.hasError) return this.props.fallback;
        return this.props.children;
    }
}

function World3DStaticFallback() {
    const { t } = useTranslation("pages:landing");
    return (
        <div className="relative flex h-full w-full flex-col items-center justify-center gap-4 rounded-3xl border border-secondary_alt/60 bg-secondary/40 px-6 py-10 backdrop-blur-sm">
            <span className="text-lg font-semibold tracking-tight text-brand-600">EveMind</span>
            <p className="max-w-sm text-center text-sm text-quaternary">{t("world.description")}</p>
        </div>
    );
}

function supportsWebGL() {
    if (typeof window === "undefined") return false;
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    return !!gl;
}

/** Allinea lo scroll alla frazione di progress attesa da useScroll offset ["start start", "end end"]. */
function scrollWorldSectionToStep(section: HTMLElement, stepIndex: number, totalSteps: number, smooth: boolean) {
    const p = totalSteps <= 1 ? 0 : stepIndex / (totalSteps - 1);
    const docTop = window.scrollY + section.getBoundingClientRect().top;
    const h = section.offsetHeight;
    const vh = window.innerHeight;
    const targetY = docTop + p * Math.max(0, h - vh);
    const y = Math.max(0, targetY);
    if (smooth) {
        window.scrollTo({ top: y, behavior: "smooth" });
    } else {
        window.scrollTo(0, y);
    }
}

export function World3DSection() {
    const { t } = useTranslation("pages:landing");
    const reducedMotion = useReducedMotion();
    const sectionRef = useRef<HTMLElement | null>(null);
    const desktopCanvasContainerRef = useRef<HTMLDivElement | null>(null);
    const mobileCanvasContainerRef = useRef<HTMLDivElement | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [cardDirection, setCardDirection] = useState(1);
    const [shouldRenderCanvas, setShouldRenderCanvas] = useState(false);
    const [hasWebGL, setHasWebGL] = useState(false);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end end"],
    });

    useMotionValueEvent(scrollYProgress, "change", (value) => {
        const nextIndex = Math.min(STOPS.length - 1, Math.max(0, Math.round(value * (STOPS.length - 1))));
        setActiveIndex((current) => {
            if (nextIndex !== current) {
                setCardDirection(nextIndex > current ? 1 : -1);
            }
            return nextIndex;
        });
    });

    useEffect(() => {
        setHasWebGL(supportsWebGL());
    }, []);

    useEffect(() => {
        const targets = [desktopCanvasContainerRef.current, mobileCanvasContainerRef.current].filter(
            Boolean,
        ) as HTMLDivElement[];
        if (targets.length === 0) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setShouldRenderCanvas(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "220px 0px" },
        );
        targets.forEach((target) => observer.observe(target));
        return () => observer.disconnect();
    }, []);

    const cards = useMemo(
        () =>
            STOPS.map((stop, index) => ({
                stop,
                index,
                title: t(`world.stops.${stop}.title`),
                description: t(`world.stops.${stop}.description`),
            })),
        [t],
    );

    const onCardActivate = useCallback(
        (index: number) => {
            setCardDirection(index > activeIndex ? 1 : -1);
            setActiveIndex(index);
            if (sectionRef.current) {
                scrollWorldSectionToStep(sectionRef.current, index, STOPS.length, !reducedMotion);
            }
        },
        [activeIndex, reducedMotion],
    );

    const onCardKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>, index: number) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onCardActivate(index);
            }
        },
        [onCardActivate],
    );

    return (
        <section id="world" ref={sectionRef} className="relative min-h-[220vh] py-16 sm:min-h-[230vh] sm:py-24">
            <div className="sticky top-20 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="mb-8 max-w-2xl">
                    <p className="text-sm font-semibold uppercase tracking-wider text-brand-600">{t("world.label")}</p>
                    <h2 className="mt-3 text-3xl font-semibold text-primary sm:text-4xl">{t("world.title")}</h2>
                    <p className="mt-4 text-base text-quaternary">{t("world.description")}</p>
                </div>

                <div className="hidden gap-8 lg:grid lg:grid-cols-2 lg:items-start">
                    <div className="space-y-3 lg:pr-2">
                        {cards.map((card) => (
                            <motion.div
                                key={card.stop}
                                role="button"
                                tabIndex={0}
                                aria-current={activeIndex === card.index ? "step" : undefined}
                                aria-label={card.title}
                                animate={{ opacity: activeIndex === card.index ? 1 : 0.4, scale: activeIndex === card.index ? 1 : 0.97 }}
                                transition={{ duration: 0.25 }}
                                className="cursor-pointer rounded-2xl border border-secondary_alt bg-primary/80 p-4 outline-none transition-colors hover:border-brand-500/30 hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
                                onClick={() => onCardActivate(card.index)}
                                onKeyDown={(e) => onCardKeyDown(e, card.index)}
                            >
                                <p className="text-lg font-semibold text-primary">{card.title}</p>
                                <p className="mt-2 text-sm text-quaternary">{card.description}</p>
                            </motion.div>
                        ))}
                    </div>

                    <div ref={desktopCanvasContainerRef} className="relative mx-auto w-full max-w-xl overflow-visible rounded-3xl">
                        <div
                            aria-hidden
                            className="pointer-events-none absolute -inset-16 rounded-full bg-[radial-gradient(circle,rgba(80,100,255,0.22)_0%,rgba(80,100,255,0.08)_34%,rgba(0,0,0,0)_72%)] blur-2xl"
                        />
                        <div className="aspect-square w-full">
                            {hasWebGL && shouldRenderCanvas && !reducedMotion ? (
                                <World3DErrorBoundary fallback={<World3DStaticFallback />}>
                                    <Suspense fallback={<div className="h-full w-full animate-pulse rounded-3xl bg-secondary/70" />}>
                                        <LazyWorld3DCanvas activeStep={activeIndex} reducedMotion={!!reducedMotion} />
                                    </Suspense>
                                </World3DErrorBoundary>
                            ) : (
                                <World3DStaticFallback />
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-5 lg:hidden">
                    <div ref={mobileCanvasContainerRef} className="relative mx-auto w-full max-w-xl overflow-visible rounded-3xl">
                        <div
                            aria-hidden
                            className="pointer-events-none absolute -inset-16 rounded-full bg-[radial-gradient(circle,rgba(80,100,255,0.22)_0%,rgba(80,100,255,0.08)_34%,rgba(0,0,0,0)_72%)] blur-2xl"
                        />
                        <div className="aspect-square w-full">
                            {hasWebGL && shouldRenderCanvas && !reducedMotion ? (
                                <World3DErrorBoundary fallback={<World3DStaticFallback />}>
                                    <Suspense fallback={<div className="h-full w-full animate-pulse rounded-3xl bg-secondary/70" />}>
                                        <LazyWorld3DCanvas activeStep={activeIndex} reducedMotion={!!reducedMotion} />
                                    </Suspense>
                                </World3DErrorBoundary>
                            ) : (
                                <World3DStaticFallback />
                            )}
                        </div>
                    </div>

                    <div className="rounded-2xl border border-secondary_alt bg-primary/80 p-4">
                        <AnimatePresence initial={false} mode="wait">
                            <motion.div
                                key={cards[activeIndex]?.stop}
                                custom={cardDirection}
                                initial={
                                    reducedMotion
                                        ? { opacity: 1 }
                                        : { opacity: 0, y: cardDirection > 0 ? 22 : -22, filter: "blur(3px)" }
                                }
                                animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, filter: "blur(0px)" }}
                                exit={
                                    reducedMotion
                                        ? { opacity: 0 }
                                        : { opacity: 0, y: cardDirection > 0 ? -22 : 22, filter: "blur(3px)" }
                                }
                                transition={{ duration: reducedMotion ? 0.15 : 0.32, ease: "easeOut" }}
                            >
                                <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">
                                    Stop {activeIndex + 1} / {cards.length}
                                </p>
                                <p className="mt-2 text-lg font-semibold text-primary">{cards[activeIndex]?.title}</p>
                                <p className="mt-2 text-sm text-quaternary">{cards[activeIndex]?.description}</p>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
}
